import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

import {
    APP_CONFIG_OFERTAS,
    IAppOfertas,
} from '../../../../app.config/app.config-ofertas';
import {
    Actividad,
    DocumentosEstadosSolicitar,
} from '@interfaces/interface-bakend/ofertas-laborales/actividad.interface';
import {
    Solicitud,
    State,
} from '@interfaces/interface-bakend/ofertas-laborales/solicitudes.interface';
import { PaisDisponible } from '@interfaces/interface-bakend/shared/paises/pais-disponible/pais-disponible.interface';
import { User } from '@interfaces/interface-bakend/seguridad/users/user.interface';
import { UserEntreUser } from '@interfaces/interface-bakend/seguridad/users/user-entre-user.interface';
import { SignoSede } from '@interfaces/interface-bakend/entidades/signo-sede.interface';

import { ActividadesService } from '@services/ofertas-laborales/actividades.service';
import { SolicitudesService } from '@services/ofertas-laborales/solicitudes.service';
import { PaisesService } from '@services/shared/paises.service';
import { AuthService } from '@services/auth/auth.service';
import { SignoSedeService } from '@services/entidades/signo-sedes.service';
import { CatalogoService } from '@services/shared/catalogo.service';
import { MediaService } from '@services/shared/media.service';
import { UsuarioService } from '@services/shared/usuario.service';
import { UserEntreUserService } from '@services/shared/user-entre-user.service';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';

import { iconoTramite } from '../../common/helpers/icono-tramite.helper';
import { RECUPERO_ACTIVIDADES } from '../../common/helpers/recupero.helper';
import {
    ACCEPT_ARCHIVOS,
    MAX_ARCHIVO_MB,
    PLACEHOLDER_HEIC,
    esHeic,
} from '../../common/helpers/archivos.helper';

interface Beneficiario {
    _id: string;
    fullName: string;
    titular: boolean;
    parentesco?: string;
}

interface DocumentoTemporal {
    file: File;
    nombre: string;
    fecha: Date;
    tempId: string;
    preview?: SafeUrl | string;
}

type Modo = 'nueva' | 'existente';
type TabActiva = 'documentacion' | 'enviados';

@Component({
    selector: 'app-solicitud-medica',
    templateUrl: './solicitud-medica.component.html',
    styleUrls: ['./solicitud-medica.component.scss'],
})
export class SolicitudMedicaComponent implements OnInit, OnDestroy {
    modo: Modo = 'existente';

    /* Archivos */
    acceptArchivos = ACCEPT_ARCHIVOS;
    maxArchivoMb = MAX_ARCHIVO_MB;

    /* Datos base */
    actividad: Actividad;
    pais: PaisDisponible;
    nombrePais: string;
    solicitud: Solicitud;
    usuario: User;
    sede: SignoSede;
    beneficiario: UserEntreUser;

    /* Beneficiarios (modo nueva) */
    beneficiarios: Beneficiario[] = [];
    beneficiarioSelected: string;
    titularId: string;
    beneficiarioId: string;
    formSolicitud: FormGroup;
    pasoNueva = 1;

    /* Documentación */
    documentosEstadosSolicitar: DocumentosEstadosSolicitar[];
    documentosTemporales: DocumentoTemporal[] = [];
    idAdjuntoExtra: any;

    /* Documentos enviados */
    files: string[] = [];
    imgs: string[] = [];

    /* UI */
    flagLoaded = false;
    flagProcesando = false;
    flagProcesandoFinalizar = false;
    flagProcesandoDocumento = false;
    loading = false;
    tabActiva: TabActiva = 'documentacion';
    descripcionExpandida = false;

    /* Estados administrativos */
    estadoDocumentacion: string;
    flagDocumentacionPresentada = false;
    flagEnRevision = false;
    flagEnAuditoria = false;
    flagAprobado = false;
    flagRechazada = false;
    activarCargaDocumentosOpcionales = false;
    activarCargaExtra = false;

    private newStateSubscription: Subscription;

    constructor(
        @Inject(APP_CONFIG_OFERTAS) private iAppOfertas: IAppOfertas,
        private route: ActivatedRoute,
        private router: Router,
        private formBuilder: FormBuilder,
        private sanitizer: DomSanitizer,
        private actividadesService: ActividadesService,
        private solicitudesService: SolicitudesService,
        private paisesService: PaisesService,
        private authService: AuthService,
        private signoSedeService: SignoSedeService,
        private catalogoService: CatalogoService,
        private mediaService: MediaService,
        private usuarioService: UsuarioService,
        private userEntreUserService: UserEntreUserService,
        private modalCustom: PopUpModalCustomService
    ) {
        this.modo = (this.route.snapshot.data['modo'] as Modo) ?? 'existente';

        this.formSolicitud = this.formBuilder.group({
            beneficiario: new FormControl('', [Validators.required]),
        });

        if (this.modo === 'existente') {
            this.newStateSubscription = this.solicitudesService
                .getsetNewStater()
                .subscribe((resp) => {
                    this.initPostChangeStatus();
                    this.reloadCurrentRoute();
                    void resp;
                });
        }
    }

    async ngOnInit(): Promise<void> {
        await this.cargarAdjuntos();
        if (this.modo === 'nueva') {
            await this.initNueva();
        } else {
            await this.initExistente();
        }
    }

    ngOnDestroy(): void {
        this.modalCustom.cerrar();
        if (this.newStateSubscription) {
            this.newStateSubscription.unsubscribe();
        }
    }

    // =====================================================
    // Spinner / feedback
    // =====================================================

    get spinnerVisible(): boolean {
        return (
            !this.flagLoaded ||
            this.loading ||
            this.flagProcesandoDocumento ||
            this.flagProcesandoFinalizar
        );
    }

    get spinnerTexto(): string {
        if (!this.flagLoaded) {
            return 'Cargando solicitud...';
        }
        if (this.loading) {
            return 'Creando solicitud, por favor espere...';
        }
        if (this.flagProcesandoDocumento) {
            return 'Subiendo documento...';
        }
        if (this.flagProcesandoFinalizar) {
            return 'Subiendo documentos...';
        }
        return '';
    }

    // =====================================================
    // Inicialización
    // =====================================================

    async initNueva(): Promise<void> {
        const actividad = this.actividadesService.getActividadSeleccionada();
        if (!actividad) {
            this.router.navigateByUrl('/');
            return;
        }
        this.actividad = actividad;
        this.getPais();
        await this.getUser();
        await this.loadSelectorBeneficiarios();
        this.flagLoaded = true;
    }

    async initExistente(): Promise<void> {
        const actividad = this.actividadesService.getActividadSeleccionada();
        const solicitud = this.solicitudesService.getSolicitudSeleccionada();
        if (!actividad || !solicitud) {
            this.router.navigateByUrl('/');
            return;
        }
        this.actividad = actividad;
        this.getPais();
        this.solicitud = solicitud;

        this.getEstadoSolicitud(this.solicitud);
        this.documentosAsolicitar(this.solicitud);
        this.getDocumentosEnviados(this.solicitud.adjuntos);
        await this.getSolicitantes();
        await this.getSede();
        await this.getBeneficiario();

        this.flagLoaded = true;
        this.dispararRecordatorioSiCorresponde();
    }

    async initPostChangeStatus(): Promise<void> {
        const solicitud = this.solicitudesService.getSolicitudSeleccionada();
        if (solicitud) {
            this.solicitud = solicitud;
            this.getEstadoSolicitud(this.solicitud);
            this.documentosAsolicitar(this.solicitud);
            this.getDocumentosEnviados(this.solicitud.adjuntos);
        }
    }

    async cargarAdjuntos(): Promise<void> {
        this.idAdjuntoExtra = await this.catalogoService.getFindByKey('ADJUNTO_EXTRA');
    }

    getPais(): PaisDisponible {
        this.pais = this.paisesService.getPaisSeleccionado();
        this.nombrePais = this.pais?.pais?.name_es;
        return this.pais;
    }

    async getUser(): Promise<User> {
        this.usuario = await this.authService.getUserLogon();
        return this.usuario;
    }

    async getSede(): Promise<void> {
        const sedeId = this.usuario?.signoSedes?.[0];
        if (sedeId !== null && sedeId !== undefined) {
            this.sede = await this.signoSedeService.getSedeById(sedeId.toString());
        }
    }

    async getSolicitantes(): Promise<void> {
        const { usuario } = this.solicitud;
        if (typeof usuario === 'string') {
            this.usuario = await this.usuarioService.getFindOne(
                this.solicitud.usuario.toString()
            );
        } else if (typeof usuario === 'object') {
            this.usuario = await this.usuarioService.getFindOne(
                this.solicitud.usuario._id
            );
        }
    }

    async getBeneficiario(): Promise<void> {
        this.beneficiario = await this.userEntreUserService.getBeneficiario(
            this.solicitud
        );
    }

    // =====================================================
    // Beneficiarios (modo nueva)
    // =====================================================

    async loadSelectorBeneficiarios(): Promise<void> {
        try {
            if (!this.usuario) {
                throw Error('Afiliado no encontrado');
            }
            const { fullName, _id, signoAdicionales, signoTitular } = this.usuario;
            this.saveBeneficiarioLogueado(fullName, _id, signoAdicionales, signoTitular);
            this.saveBeneficarioAdicional(_id, signoAdicionales);
        } catch (error) {
            console.error('Error al cargar beneficiarios', error);
        }
    }

    saveBeneficiarioLogueado(
        fullName: string,
        identificacionAfiliadoLogged: string,
        adicionalesArray: UserEntreUser[],
        titularArray: UserEntreUser[]
    ): void {
        const esTitular =
            titularArray.length === 0 ||
            (titularArray.length === 0 && adicionalesArray.length === 0);

        const dataBeneficiarioLogueado: Beneficiario = esTitular
            ? {
                  _id: identificacionAfiliadoLogged,
                  fullName,
                  titular: true,
                  parentesco: 'TITULAR',
              }
            : {
                  _id: identificacionAfiliadoLogged,
                  fullName,
                  titular: false,
                  parentesco: titularArray[0].parentesco
                      ? titularArray[0].parentesco
                      : adicionalesArray[0].parentesco,
              };

        this.beneficiarios.push(dataBeneficiarioLogueado);
    }

    saveBeneficarioAdicional(
        identificacionAfiliadoLogged: string,
        adicionalesArray: UserEntreUser[]
    ): void {
        if (!adicionalesArray?.length) {
            return;
        }
        const adicionales = adicionalesArray.map((item) => ({
            adicional: item.adicional,
            parentesco: item.parentesco,
            titular: item.titular,
        }));
        adicionales.forEach((adicional) => {
            const adicionalParsed = adicional.adicional.toString();
            const titular = adicionalParsed === identificacionAfiliadoLogged;
            this.usuarioService
                .getFindOne(adicionalParsed)
                .then((afiliado: User) => {
                    this.beneficiarios.push({
                        _id: afiliado._id,
                        fullName: afiliado.fullName,
                        titular,
                        parentesco: adicional.parentesco,
                    });
                })
                .catch((error) => {
                    console.error('Error al obtener familiares a cargo', error);
                });
        });
    }

    onBeneficiarioChange(event: Event): void {
        this.beneficiarioSelected = (event.target as HTMLSelectElement).value;
        this.onBenefactorSelectedChange(this.beneficiarios);
    }

    onBenefactorSelectedChange(beneficiarios: Beneficiario[]): void {
        const existeBeneficiarios =
            beneficiarios && beneficiarios.length > 0 && this.beneficiarioSelected;
        if (!existeBeneficiarios) {
            return;
        }

        const tipoBeneficiario = beneficiarios.find(
            (beneficiario) => beneficiario._id === this.beneficiarioSelected
        );
        if (!tipoBeneficiario) {
            return;
        }

        const titularATitular =
            tipoBeneficiario.titular && tipoBeneficiario._id === this.usuario._id;
        const titularABeneficiario = !tipoBeneficiario.titular;
        const beneficiario =
            !tipoBeneficiario.titular && tipoBeneficiario._id === this.usuario._id;

        if (titularATitular) {
            this.titularId = this.beneficiarioSelected;
            this.beneficiarioId = this.beneficiarioSelected;
        } else if (titularABeneficiario || beneficiario) {
            const adicionalTitular = this.usuario?.signoAdicionales?.[0]?.titular;
            const titular = this.usuario?.signoTitular?.[0]?.titular;
            this.titularId = adicionalTitular
                ? adicionalTitular.toString()
                : titular.toString();
            this.beneficiarioId = this.beneficiarioSelected;
        }
    }

    get beneficiarioLabel(): string {
        const encontrado = this.beneficiarios.find(
            (b) => b._id === this.beneficiarioSelected
        );
        return encontrado ? `${encontrado.fullName} - ${encontrado.parentesco}` : '';
    }

    irAResumen(): void {
        if (this.formSolicitud.invalid) {
            return;
        }
        this.pasoNueva = 2;
    }

    volverADatos(): void {
        this.pasoNueva = 1;
    }

    // =====================================================
    // Creación de la solicitud (modo nueva)
    // =====================================================

    async procesarSolicitudAutorizacion(): Promise<void> {
        if (!this.usuario?.signoSedes?.length) {
            this.modalCustom.abrirPopup({
                tipo: 'error',
                titulo: 'Sin sede asignada',
                mensaje:
                    'No tiene una sede asignada. Comuníquese con administración, sepa disculpar.',
            });
            return;
        }

        const signoSede = this.usuario.signoSedes[0];
        this.flagProcesando = true;
        this.loading = true;

        this.solicitud = {
            actividad: this.actividadesService.getActividadSeleccionada()._id,
            pais: this.paisesService.getPaisSeleccionado().pais._id,
            usuario: (await this.authService.getUserLogon())._id,
            beneficiario: this.beneficiarioId ? this.beneficiarioId : null,
            titular: this.titularId ? this.titularId : null,
            state: { numberState: this.iAppOfertas.estado_0.number },
            type: this.iAppOfertas.tipo_constantes.name,
            documentosEstadosSolicitar:
                this.actividadesService.getActividadSeleccionada().documentosEstadosSolicitar,
            signoSede: signoSede !== undefined ? { _id: signoSede } : null,
        } as any;

        this.solicitudesService
            .create(this.solicitud)
            .then((response) => {
                this.flagProcesando = false;
                this.loading = false;
                this.solicitudesService.setSolicitudSeleccionada(response);
                this.router.navigateByUrl('/solicitud');
            })
            .catch(() => {
                this.flagProcesando = false;
                this.loading = false;
                this.modalCustom.abrirPopup({
                    tipo: 'error',
                    titulo: 'No se pudo crear la solicitud',
                    mensaje:
                        'Ocurrió un error al iniciar la solicitud. Verificá tu conexión e intentá nuevamente.',
                });
            });
    }

    // =====================================================
    // Estado de la solicitud
    // =====================================================

    getEstadoSolicitud(solicitud: Solicitud): void {
        const numberState = solicitud?.lastState?.numberState;
        if (numberState === undefined || numberState === null) {
            return;
        }

        switch (numberState) {
            case 0:
            case 1:
                this.activarCargaDocumentosOpcionales = true;
                this.activarCargaExtra = true;
                this.flagDocumentacionPresentada = false;
                this.flagEnRevision = false;
                this.estadoDocumentacion = this.iAppOfertas.documentacion.pendiente;
                break;
            case 2:
                this.activarCargaDocumentosOpcionales = false;
                this.activarCargaExtra = true;
                this.flagDocumentacionPresentada = true;
                this.flagEnRevision = true;
                this.flagAprobado = false;
                this.estadoDocumentacion = this.iAppOfertas.documentacion.completo;
                break;
            case 3:
                this.activarCargaDocumentosOpcionales = false;
                // Auditoría médica no admite adjuntos (candado del back y paridad
                // con la app): no se renderiza el selector de documentación extra.
                this.activarCargaExtra = false;
                this.flagDocumentacionPresentada = true;
                this.flagAprobado = false;
                this.flagEnRevision = false;
                this.flagEnAuditoria = true;
                this.estadoDocumentacion = this.iAppOfertas.documentacion.completo;
                break;
            case 4:
                this.activarCargaDocumentosOpcionales = false;
                this.activarCargaExtra = false;
                this.flagEnAuditoria = false;
                this.flagAprobado = true;
                this.flagDocumentacionPresentada = false;
                this.estadoDocumentacion = this.iAppOfertas.documentacion.completo;
                break;
            case 5:
                this.activarCargaDocumentosOpcionales = false;
                this.activarCargaExtra = false;
                this.flagDocumentacionPresentada = false;
                this.flagAprobado = false;
                this.flagRechazada = true;
                this.estadoDocumentacion = this.iAppOfertas.documentacion.completo;
                break;
            case 6:
            case 7:
                // En espera (6) y recupero (7) el trámite está abierto pero NO
                // admite documentación adicional: mismo criterio que el candado
                // del back (ESTADOS_CON_ADJUNTOS = 0/1/2) y que la app, que ahí
                // ni deja seleccionar archivos. Sin selector renderizado.
                this.activarCargaDocumentosOpcionales = false;
                this.activarCargaExtra = false;
                this.flagDocumentacionPresentada = false;
                break;
            default:
                this.activarCargaDocumentosOpcionales = false;
                this.activarCargaExtra = false;
                this.flagDocumentacionPresentada = false;
                break;
        }
    }

    get estadoNombre(): string {
        return this.getNombreEstado(this.solicitud?.lastState?.numberState);
    }

    get chipEstado(): string {
        switch (this.solicitud?.lastState?.numberState) {
            case 4:
                return 'ds-chip--aprobado';
            case 3:
                return 'ds-chip--auditoria';
            case 2:
                return 'ds-chip--revision';
            case 7:
                return 'ds-chip--recupero';
            case 6:
                return 'ds-chip--espera';
            case 5:
                return 'ds-chip--anulado';
            default:
                return 'ds-chip--neutro';
        }
    }

    get iconoTramite(): string {
        return iconoTramite(this.actividad);
    }

    /** Evita renderizar bloques con texto vacío o solo espacios. */
    tieneTexto(valor: string | undefined | null): boolean {
        return !!valor && valor.trim().length > 0;
    }

    /** Si la descripción es muy larga, se colapsa con "Ver más". */
    get descripcionEsLarga(): boolean {
        const largo =
            (this.actividad?.descripcion?.trim().length ?? 0) +
            (this.actividad?.detalle?.trim().length ?? 0);
        return largo > 600;
    }

    get mostrarConfirmacion(): boolean {
        return this.modo === 'existente' && this.solicitud?.lastState?.numberState === 0;
    }

    /**
     * true solo en PRESENTADO (0) o DOCUMENTACIÓN PRESENTADA (1): ahí el botón
     * "Finalizar carga" envía el trámite a revisión. En el resto solo adjunta
     * documentación adicional.
     */
    get puedeEnviarARevision(): boolean {
        const numberState = this.solicitud?.lastState?.numberState;
        return (
            numberState === this.iAppOfertas.estado_0.number ||
            numberState === this.iAppOfertas.estado_1.number
        );
    }

    get textoFinalizar(): string {
        return this.puedeEnviarARevision
            ? 'Finalizar carga'
            : 'Enviar documentación adicional';
    }

    /** Trámite del circuito Recupero (actividad habilitada o ya en estado 7). */
    get esTramiteRecupero(): boolean {
        return (
            this.solicitud?.lastState?.numberState === this.iAppOfertas.estado_7.number ||
            (!!this.actividad?._id &&
                RECUPERO_ACTIVIDADES.includes(this.actividad._id))
        );
    }

    /**
     * Estado que muestra el stepper. En EN ESPERA (6) se muestra el estado
     * previo al último 6 (soporta esperas encadenadas), igual que la bandeja.
     */
    get estadoParaStepper(): number {
        const actual =
            this.solicitud?.lastState?.numberState ?? this.iAppOfertas.estado_0.number;
        if (actual !== this.iAppOfertas.estado_6.number) {
            return actual;
        }
        const states = this.solicitud?.states ?? [];
        let i = states.length - 1;
        while (
            i >= 0 &&
            states[i]?.numberState === this.iAppOfertas.estado_6.number
        ) {
            i--;
        }
        return i >= 0 ? states[i].numberState : this.iAppOfertas.estado_2.number;
    }

    get historial(): State[] {
        const states = this.solicitud?.states ?? [];
        if (states.length) {
            return [...states].reverse();
        }
        return this.solicitud?.lastState ? [this.solicitud.lastState] : [];
    }

    getNombreEstado(numberState: number): string {
        const estado = this.iAppOfertas.selectEstados.find(
            (e) => e.number === numberState
        );
        return estado ? estado.name : 'DESCONOCIDO';
    }

    // =====================================================
    // Documentación
    // =====================================================

    documentosAsolicitar(solicitud: Solicitud): DocumentosEstadosSolicitar[] {
        this.documentosEstadosSolicitar = solicitud.documentosEstadosSolicitar;
        return this.documentosEstadosSolicitar;
    }

    getDocumentosEnviados(adjuntos: string[]): void {
        const lista = adjuntos ?? [];
        this.files = lista.filter((element) => element.search('.pdf') !== -1);
        this.imgs = lista.filter((element) => element.search('.pdf') === -1);
    }

    controlEntregaTotalDocumentos(solicitud: Solicitud): boolean {
        const docs = solicitud.documentosEstadosSolicitar ?? [];
        if (!docs.length) {
            return false;
        }
        return docs.every((elemento) => elemento.entregado === true);
    }

    /**
     * Subida directa de un documento requerido (sin modal): abre el selector
     * nativo y sube el archivo al adjunto correspondiente.
     */
    async onFileSelectedDocumento(
        event: Event,
        doc: DocumentosEstadosSolicitar
    ): Promise<void> {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            return;
        }
        if (this.flagProcesandoDocumento) {
            input.value = '';
            return;
        }

        let file = input.files[0];

        if (!this.mediaService.controlSize(event)) {
            input.value = '';
            this.modalCustom.abrirPopup({
                tipo: 'warning',
                titulo: 'Archivo demasiado grande',
                mensaje: `El archivo excede el tamaño máximo permitido de ${this.maxArchivoMb} MB.`,
            });
            return;
        }

        // HEIC/HEIF se suben crudos (el backend los convierte a WebP).
        if (!esHeic(file) && file.type.startsWith('image/')) {
            file = await this.resizeImagePromise(file, 1024, 1024);
        }

        this.flagProcesandoDocumento = true;
        try {
            const idUsuario = this.getUsuarioId();
            const resp = await this.mediaService.uploadOneAttachJobOfferFromWeb(
                file,
                idUsuario,
                doc.documento._id,
                this.solicitud._id,
                false
            );

            if (resp && resp.ok) {
                this.solicitudesService.setSolicitudSeleccionada(resp.solicitud);
                await this.onDocumentoCargado({ idAdjunto: doc.documento._id });
            } else {
                this.modalCustom.abrirPopup({
                    tipo: 'error',
                    titulo: 'No se pudo subir el archivo',
                    mensaje: 'Ocurrió un error al subir el documento. Intentá nuevamente.',
                });
            }
        } catch (error) {
            this.modalCustom.abrirPopup({
                tipo: 'error',
                titulo: 'No se pudo subir el archivo',
                mensaje:
                    'Ocurrió un error al subir el documento. Verificá tu conexión e intentá nuevamente.',
            });
        } finally {
            this.flagProcesandoDocumento = false;
            input.value = '';
        }
    }

    async onDocumentoCargado(resp: any): Promise<void> {
        if (!resp?.idAdjunto) {
            return;
        }
        this.flagProcesandoDocumento = true;
        try {
            const solicitud = await this.solicitudesService.getSolicitudSeleccionada();
            if (!solicitud) {
                return;
            }
            this.solicitud = await this.actualizarSolicitudDocumentoCargado(
                solicitud,
                resp.idAdjunto
            );
            await this.actualizarEstadoSolicitudDocumentacionCompleta(this.solicitud);
            this.documentosEstadosSolicitar = this.solicitud.documentosEstadosSolicitar;
        } finally {
            this.flagProcesandoDocumento = false;
        }
    }

    async actualizarSolicitudDocumentoCargado(
        solicitud: Solicitud,
        idAdjunto: string
    ): Promise<Solicitud> {
        await this.findDocumentoCargado(solicitud, idAdjunto);
        this.solicitud = await this.solicitudesService.updateSolicitud(
            solicitud._id,
            solicitud
        );
        return this.solicitud;
    }

    async findDocumentoCargado(solicitud: any, idAdjunto: string): Promise<Solicitud> {
        solicitud.documentosEstadosSolicitar.forEach((elemento) => {
            if (elemento.documento._id === idAdjunto) {
                elemento.entregado = true;
            }
        });
        if (solicitud.pais?.hasOwnProperty('_id')) {
            solicitud.pais = solicitud.pais._id;
        }
        if (solicitud.actividad?.hasOwnProperty('_id')) {
            solicitud.actividad = solicitud.actividad._id;
        }
        if (solicitud.usuario?.hasOwnProperty('_id')) {
            solicitud.usuario = solicitud.usuario._id;
        }
        return solicitud;
    }

    async actualizarEstadoSolicitudDocumentacionCompleta(
        solicitud: Solicitud
    ): Promise<void> {
        // Subir documentación NUNCA manda a revisión: el trámite avanza a
        // DOCUMENTACIÓN PRESENTADA (1) y se queda ahí. El paso a EN REVISIÓN (2)
        // lo decide el afiliado con el botón "Finalizar carga".
        const numberState = solicitud?.lastState?.numberState;

        if (numberState === this.iAppOfertas.estado_0.number) {
            const solicitudUpdated = await this.solicitudesService.updateSolicitudState(
                solicitud._id,
                this.iAppOfertas.estado_1.number,
                false,
                this.iAppOfertas.estado_1.name
            );
            this.solicitud = solicitudUpdated;
            this.solicitudesService.setSolicitudSeleccionada(solicitudUpdated);
            this.modalCustom.abrirPopup({
                tipo: 'success',
                titulo: 'Documentación presentada',
                mensaje:
                    'Guardamos el documento. Cuando tengas toda la documentación, presioná "Finalizar carga" para enviar el trámite a revisión.',
                textoBoton: 'Entendido',
                accionConfirmar: () => {
                    this.solicitudesService.setNewState(solicitudUpdated);
                    this.reloadCurrentRoute();
                },
            });
            return;
        }

        // Ya está en 1 (o más): subir no cambia el estado, solo confirmamos.
        this.modalCustom.abrirPopup({
            tipo: 'success',
            titulo: 'Documento recibido',
            mensaje: this.controlEntregaTotalDocumentos(solicitud)
                ? 'Documentación completa. Presioná "Finalizar carga" para enviar el trámite a revisión.'
                : 'Guardamos el documento. Podés seguir cargando el resto.',
            textoBoton: 'Entendido',
        });
    }

    // =====================================================
    // Carga directa de archivos (documentación extra)
    // =====================================================

    async onFileSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            return;
        }

        let file = input.files[0];

        if (!this.mediaService.controlSize(event)) {
            input.value = '';
            this.modalCustom.abrirPopup({
                tipo: 'warning',
                titulo: 'Archivo demasiado grande',
                mensaje: `El archivo excede el tamaño máximo permitido de ${this.maxArchivoMb} MB.`,
            });
            return;
        }

        // HEIC/HEIF: el navegador no los decodifica. Se sube crudo (el backend
        // los convierte a WebP) y el preview muestra un placeholder.
        if (esHeic(file)) {
            this.documentosTemporales.push({
                file,
                nombre: file.name,
                fecha: new Date(),
                tempId: this.generarTempId(),
                preview: this.sanitizer.bypassSecurityTrustUrl(PLACEHOLDER_HEIC),
            });
            input.value = '';
            return;
        }

        if (file.type.startsWith('image/')) {
            file = await this.resizeImagePromise(file, 1024, 1024);
        }

        const preview = await this.fileToBase64(file);

        this.documentosTemporales.push({
            file,
            nombre: file.name,
            fecha: new Date(),
            tempId: this.generarTempId(),
            preview,
        });

        input.value = '';
    }

    resizeImagePromise(file: File, maxWidth: number, maxHeight: number): Promise<File> {
        return new Promise((resolve) => {
            const outputType = file.type;
            const outputName = file.name;
            const reader = new FileReader();

            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result as string;

                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    let width = img.width;
                    let height = img.height;

                    if (width > height && width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    } else if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob(
                        (blob) => {
                            resolve(new File([blob], outputName, { type: outputType }));
                        },
                        outputType,
                        0.8
                    );
                };

                img.onerror = () => resolve(file);
            };

            reader.onerror = () => resolve(file);
            reader.readAsDataURL(file);
        });
    }

    fileToBase64(file: File): Promise<string> {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
        });
    }

    eliminarDocumentoTemp(id: string): void {
        this.documentosTemporales = this.documentosTemporales.filter(
            (doc) => doc.tempId !== id
        );
    }

    getUsuarioId(): string {
        const usuario: any = this.solicitud.usuario;
        if (usuario && typeof usuario === 'object' && usuario._id) {
            return usuario._id as string;
        }
        return String(usuario);
    }

    async finalizarCargaDocumentosSolicitud(event?: Event): Promise<void> {
        event?.preventDefault();

        if (this.flagProcesandoFinalizar) {
            return;
        }

        // La carga de documentación (requerida o extra) solo está abierta
        // mientras el trámite no esté resuelto (4 = APROBADO, 5 = ANULADO).
        if (!this.activarCargaExtra) {
            return;
        }

        this.flagProcesandoFinalizar = true;

        try {
            const idUsuario = this.getUsuarioId();
            const idAdjuntoExtra = this.idAdjuntoExtra?.properties?._idAdjunto;

            for (const doc of this.documentosTemporales) {
                await this.mediaService.uploadOneAttachJobOfferFromWeb(
                    doc.file,
                    idUsuario,
                    idAdjuntoExtra,
                    this.solicitud._id,
                    false
                );
            }

            this.documentosTemporales = [];

            // El paso a EN REVISIÓN (2) solo ocurre desde PRESENTADO (0) o
            // DOCUMENTACIÓN PRESENTADA (1). Desde 2/3/6/7 esto solo adjunta
            // documentación adicional y NO cambia el estado.
            if (this.puedeEnviarARevision) {
                const sol = await this.solicitudesService.updateSolicitudState(
                    this.solicitud._id,
                    this.iAppOfertas.estado_2.number,
                    true,
                    this.iAppOfertas.estado_2.name
                );

                this.solicitud = sol;
                this.solicitudesService.setSolicitudSeleccionada(sol);

                this.modalCustom.abrirPopup({
                    tipo: 'success',
                    variante: 'solicitud',
                    titulo: 'Documentación enviada',
                    mensaje:
                        '<p class="popup-lead">Tu solicitud pasó a <strong>Revisión</strong>.</p>' +
                        '<div class="popup-alert-card">' +
                        '<div class="popup-alert-card__header">' +
                        '<span class="popup-alert-card__badge"><i class="fas fa-exclamation-circle"></i> IMPORTANTE</span>' +
                        '</div>' +
                        '<div class="popup-alert-card__body">' +
                        '<i class="fas fa-clock popup-alert-card__icon"></i>' +
                        '<div class="popup-alert-card__text">' +
                        '<strong>Plazo de autorización:</strong> hasta <strong>5 (cinco) días hábiles</strong> desde su carga.' +
                        '</div>' +
                        '</div>' +
                        '</div>' +
                        '<p class="popup-steps-title">¿Qué pasa ahora?</p>' +
                        '<ul class="popup-steps">' +
                        '<li>Verificamos tus archivos y datos.</li>' +
                        '<li>Si falta algo, te avisamos por email.</li>' +
                        '<li>Recibirás novedades en tu correo.</li>' +
                        '</ul>',
                    textoBoton: 'ACEPTAR',
                    accionConfirmar: () => this.router.navigateByUrl('/'),
                });
                return;
            }

            this.modalCustom.abrirPopup({
                tipo: 'success',
                titulo: 'Documentación adicional enviada',
                mensaje:
                    'Adjuntamos la documentación adicional. El estado del trámite no cambia.',
                textoBoton: 'Entendido',
                accionConfirmar: () => this.reloadCurrentRoute(),
            });
        } catch (error: any) {
            // 409 = el back rechazó el adjunto porque el estado del trámite ya
            // no lo admite (media.controller.ts -> rechazoPorEstado). No es una
            // falla: se avisa como advertencia y sin nombrar el estado interno
            // (recupero, auditoría, espera), que el afiliado no conoce.
            const estadoNoPermitido = error?.status === 409;

            this.modalCustom.abrirPopup({
                tipo: estadoNoPermitido ? 'warning' : 'error',
                titulo: estadoNoPermitido
                    ? 'No se puede adjuntar documentación'
                    : 'No se pudieron subir los documentos',
                mensaje: estadoNoPermitido
                    ? 'El trámite se encuentra en un estado que no admite documentación adicional. Si necesitás sumar un respaldo, contactate con nosotros.'
                    : 'Ocurrió un error al enviar la documentación. Verificá tu conexión e intentá nuevamente.',
                textoBoton: 'Entendido',
            });
        } finally {
            this.flagProcesandoFinalizar = false;
        }
    }

    // =====================================================
    // Recordatorio
    // =====================================================

    msjRecordatorioFinalizarSolicitud(): void {
        if (this.flagAprobado || this.flagRechazada) {
            return;
        }

        this.modalCustom.abrirPopup({
            tipo: 'info',
            titulo: 'Recordatorio importante',
            mensaje:
                '<p>Para que tu solicitud <strong>sea tratada</strong> y pueda avanzar, es necesario que cargues los documentos correspondientes.</p>' +
                '<p>Si no finalizas la solicitud, <strong>tu trámite no tendrá curso</strong> y no podrá ser revisado.</p>',
            textoBoton: 'Entendido',
        });
    }

    private dispararRecordatorioSiCorresponde(): void {
        if (!this.solicitud || !this.solicitud.lastState) {
            return;
        }
        const estado = this.solicitud.lastState.numberState;
        if (this.flagAprobado || this.flagRechazada || estado >= 1) {
            return;
        }
        setTimeout(() => this.msjRecordatorioFinalizarSolicitud(), 500);
    }

    // =====================================================
    // Helpers
    // =====================================================

    private generarTempId(): string {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }

    reloadCurrentRoute(): void {
        this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() =>
                this.router.navigate([
                    this.modo === 'nueva' ? 'solicitud-tramites' : 'solicitud',
                ])
            );
    }

    trackByDocumento(index: number, doc: DocumentoTemporal): string {
        return doc.tempId;
    }
}
